import { useEffect, useMemo, useRef } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { ArrowRight, BriefcaseBusiness, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { useCreateInvoice } from '@/hooks/invoices/useCreateInvoice';
import { useIsFieldHidden } from '@/hooks/usePermissions';

export const Route = createFileRoute('/_dashboard/dashboard/invoices/new')({
  component: NewInvoicePage,
});

const CREATE_INVOICE_LOCK_PREFIX = 'invoice-create-lock:';

/**
 * Auto-creates a blank invoice and immediately redirects to the detail editor.
 * Mirrors the "New Invoice" button on the list page for direct URL access.
 */
function NewInvoicePage() {
  const router = useRouter();
  const pricingHidden = useIsFieldHidden('PRICING');
  const createInvoice = useCreateInvoice();
  const createdRef = useRef(false);
  const jobId = useMemo(
    () => new URLSearchParams(window.location.search).get('jobId') ?? undefined,
    [],
  );

  useEffect(() => {
    if (pricingHidden) return;
    if (createdRef.current) return;
    if (!jobId) return;
    const lockKey = `${CREATE_INVOICE_LOCK_PREFIX}${jobId}`;
    if (window.sessionStorage.getItem(lockKey)) return;
    createdRef.current = true;
    window.sessionStorage.setItem(lockKey, '1');
    createInvoice.mutate(
      { jobId },
      {
        onSuccess: (invoice) => {
          window.sessionStorage.removeItem(lockKey);
          // @ts-expect-error — search required by TanStack Router v1.168
          void router.navigate({
            to: '/dashboard/invoices/$id',
            params: { id: invoice.id },
            replace: true,
          });
        },
        onError: (error) => {
          window.sessionStorage.removeItem(lockKey);
          toast.error(error instanceof Error ? error.message : 'Failed to create invoice');
          void router.navigate({
            to: '/dashboard/invoices',
            search: { status: 'all', q: '', page: 1 } as never,
            replace: true,
          });
        },
      },
    );
  }, [createInvoice, jobId, pricingHidden, router]);

  if (pricingHidden) {
    return (
      <div className="page-enter mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="font-medium">Invoice creation is unavailable for your role.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            You can still review invoice status, customer details, and line-item descriptions.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            void router.navigate({
              to: '/dashboard/invoices',
              search: { status: 'all', q: '', page: 1 } as never,
            })
          }
        >
          Back to invoices
        </Button>
      </div>
    );
  }

  if (!jobId) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>Create Invoice</CardTitle>
            <CardDescription>
              Operations creates invoices from a job. Start from a job record or pass a `jobId` when
              linking to this route.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button
              onClick={() =>
                void router.navigate({
                  to: '/dashboard/jobs',
                  search: { q: '', page: 1 },
                })
              }
            >
              <BriefcaseBusiness className="mr-2 h-4 w-4" />
              Open Jobs
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                void router.navigate({
                  to: '/dashboard/invoices',
                  search: { status: 'all', q: '', page: 1 } as never,
                })
              }
            >
              Back to invoices
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span className="text-sm">Creating invoice…</span>
    </div>
  );
}
